import axiosInstance from './axiosInstance';

/**
 * Subscription Service
 * Handles all subscription-related API calls
 */
export const SubscriptionService = {
	/**
	 * Create checkout session for subscription (subscribe, upgrade, or downgrade)
	 * @param {string} priceId - Stripe Price ID
	 * @param {string} successUrl - URL to redirect after successful payment
	 * @param {string} cancelUrl - URL to redirect if payment is canceled
	 * @returns {Promise<object>} - Checkout session with URL
	 */
	createCheckoutSession: async (priceId, successUrl, cancelUrl) => {
		try {
			const response = await axiosInstance.post('/subscription/checkout', {
				priceId,
				successUrl,
				cancelUrl
			});

			return {
				status: response.status,
				data: response.data,
				...response.data
			};
		} catch (error) {
			throw error;
		}
	},

	/**
	 * Get current subscription details
	 * @returns {Promise<object>} - Current subscription information
	 */
	getCurrentSubscription: async () => {
		try {
			const response = await axiosInstance.get('/subscription/current');
			return {
				status: response.status,
				data: response.data,
				...response.data
			};
		} catch (error) {
			throw error;
		}
	},

	/**
	 * Create customer portal session for subscription management
	 * @param {string} returnUrl - URL to return to after portal session
	 * @returns {Promise<object>} - Portal session with URL
	 */
	createPortalSession: async (returnUrl) => {
		try {
			const response = await axiosInstance.post('/subscription/portal', {
				returnUrl
			});

			return {
				status: response.status,
				data: response.data,
				...response.data
			};
		} catch (error) {
			throw error;
		}
	}
};

/**
 * Plan Configuration
 * This should match your backend config/plans.js
 * Update the price IDs with your actual Stripe Price IDs
 */
export const PLANS = {
	scholar: {
		name: 'Scholar',
		description: 'Perfect for students getting started',
		priceIds: {
			monthly: 'price_1SmFrERyOqBDhF3Z3zsivcRq', // Replace with actual Price ID
			quarterly: 'price_1SmG0RRyOqBDhF3ZROid7vXF', // Replace with actual Price ID
			yearly: 'price_1SmG0QRyOqBDhF3Z55Kuz7TZ' // Replace with actual Price ID
		},
		features: {
			maxDocuments: 3,
			maxCitationsPerDocument: 10,
			allowedExportFormats: ['PDF'],
			hasWatermark: true,
			canExport: true,
			canUseAdvancedFeatures: false,
			canUseAISuggestions: false,
			canUseTemplates: false,
			maxDocumentPages: 50
		}
	},
	fellow: {
		name: 'Fellow',
		description: 'For serious academic writers',
		priceIds: {
			monthly: 'price_1SnNsa2MKaHUEiZ3rXXgzOsN', // Replace with actual Price ID
			quarterly: 'price_1SnNtV2MKaHUEiZ3eC6QgVN9', // Replace with actual Price ID
			yearly: 'price_1SnNuI2MKaHUEiZ30bFXNeH5' // Replace with actual Price ID
		},
		features: {
			maxDocuments: 10,
			maxCitationsPerDocument: 50,
			allowedExportFormats: ['PDF', 'DOCX'],
			hasWatermark: false,
			canExport: true,
			canUseAdvancedFeatures: true,
			canUseAISuggestions: false,
			canUseTemplates: true,
			maxDocumentPages: 200
		}
	},
	laureate: {
		name: 'Laureate',
		description: 'Unlimited access for professionals',
		priceIds: {
			monthly: 'price_1SnNxz2MKaHUEiZ3NCRiAKwg', // Replace with actual Price ID
			quarterly: 'price_1SnNyy2MKaHUEiZ3mQo4myLr', // Replace with actual Price ID
			yearly: 'price_1SnNzT2MKaHUEiZ3lbtbCHLe' // Replace with actual Price ID
		},
		features: {
			maxDocuments: -1, // Unlimited
			maxCitationsPerDocument: -1, // Unlimited
			allowedExportFormats: ['PDF', 'DOCX', 'HTML'],
			hasWatermark: false,
			canExport: true,
			canUseAdvancedFeatures: true,
			canUseAISuggestions: true,
			canUseTemplates: true,
			maxDocumentPages: -1 // Unlimited
		}
	}
};

/**
 * Get plan by name
 * @param {string} planName - Plan name (scholar, fellow, laureate)
 * @returns {object|null} - Plan object or null
 */
export function getPlanByName(planName) {
	return PLANS[planName] || null;
}

/**
 * Get price ID for a plan and billing interval
 * @param {string} planName - Plan name
 * @param {string} interval - Billing interval (monthly, quarterly, yearly)
 * @returns {string|null} - Price ID or null
 */
export function getPriceId(planName, interval) {
	const plan = getPlanByName(planName);
	if (!plan) return null;

	const intervalMap = {
		monthly: 'monthly',
		quarterly: 'quarterly',
		yearly: 'yearly'
	};

	const mappedInterval = intervalMap[interval] || interval;
	return plan.priceIds[mappedInterval] || null;
}
